import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Scales } from "@phosphor-icons/react";
import { formatDate } from "@/lib/format";

/**
 * Renders the currently-active FCA disclaimer for a given slot.
 * Slots: "header" | "footer" | "confidential" | "sign_in"
 * Wording is fetched from the backend so admins can edit it without touching code.
 * The backend automatically picks the audience-specific version based on the
 * viewer's investor classification.
 */
export default function FcaDisclaimerBand({ slot, tone = "default", companyName }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get(`/disclaimers/${slot}`).then((r) => setD(r.data)).catch(() => setD(null));
  }, [slot]);
  if (!d) return null;

  const body = (d.body || "").replace(/\{\{company_name\}\}/g, companyName || "the company");
  const year = new Date().getFullYear();

  if (slot === "header" || tone === "header") {
    return (
      <div className="rounded-xl border-l-4 border-amber-500 bg-amber-50/60 dark:bg-amber-950/20 px-5 py-4 my-6" data-testid={`disclaimer-${slot}`}>
        <div className="flex items-start gap-3">
          <Scales size={18} className="text-amber-700 dark:text-amber-400 shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-amber-800 dark:text-amber-300 mb-2">
              {d.title || "Confidential — Professional Clients Only"}
            </div>
            <div className="text-xs text-amber-900/90 dark:text-amber-100/90 leading-relaxed whitespace-pre-line">
              {body}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Footer — lightweight regulatory strip (compact one-liner + audit meta)
  return (
    <div className="rounded-xl border border-border bg-secondary/30 px-5 py-4 mt-10 text-[11px] text-muted-foreground leading-relaxed" data-testid={`disclaimer-${slot}`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-[260px] whitespace-pre-line">
          {body}
        </div>
        <div className="text-right shrink-0 space-y-0.5">
          <div className="uppercase tracking-wider text-[9px] font-bold text-muted-foreground/70">Document</div>
          <div className="num-display">v{d.version} · Effective {formatDate(d.effective_at)}</div>
          <div className="text-muted-foreground/70">© {year} Inventya Capital Ltd</div>
        </div>
      </div>
    </div>
  );
}
